import * as React from 'react';
import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection, AdminStatCard } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

export default function AdminWorkspacesPage(props) {
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let active = true;

    async function loadWorkspaces() {
      try {
        const response = await axiosInstance.get('/admin/workspaces');
        if (!active) {
          return;
        }
        setState({ loading: false, error: '', data: response.data });
      } catch (error) {
        if (!active) {
          return;
        }
        setState({
          loading: false,
          error: error?.response?.data?.message || 'Unable to load workspace/message admin stats.',
          data: null,
        });
      }
    }

    loadWorkspaces();
    return () => {
      active = false;
    };
  }, []);

  const summary = state.data?.summary || {};

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'Workspaces & Messages' }]}
      title="Workspaces & Messages"
      subtitle="Cross-tenant workspace volumes, partner activity, and messaging trends."
    >
      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading workspace metrics...</Typography>
        </Stack>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <AdminStatCard label="Total Workspaces" value={summary.workspace_count || 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <AdminStatCard label="Active Workspaces" value={summary.active_workspace_count || 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <AdminStatCard label="Archived Workspaces" value={summary.archived_workspace_count || 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <AdminStatCard label="Messages" value={summary.message_count || 0} />
            </Grid>
          </Grid>

          <AdminSection title="Most Active Partners" subtitle="Partners generating the most workspace and messaging activity.">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Workspaces</TableCell>
                  <TableCell align="right">Messages</TableCell>
                  <TableCell>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(state.data?.top_partners || []).map((partner) => (
                  <TableRow key={partner.partner_id} hover>
                    <TableCell>{partner.display_name}</TableCell>
                    <TableCell align="right">{partner.workspace_count}</TableCell>
                    <TableCell align="right">{partner.message_count}</TableCell>
                    <TableCell>{partner.last_activity_at || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AdminSection>

          <AdminSection title="Recent Workspaces" subtitle="Latest workspace updates visible from the system-wide admin view.">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Messages</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(state.data?.recent_workspaces || []).map((workspace) => (
                  <TableRow key={workspace.workspace_id} hover>
                    <TableCell>{workspace.title}</TableCell>
                    <TableCell>{workspace.partner_display_name}</TableCell>
                    <TableCell>{workspace.status}</TableCell>
                    <TableCell align="right">{workspace.message_count}</TableCell>
                    <TableCell>{workspace.updated_at || workspace.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AdminSection>
        </>
      )}
    </AdminLayout>
  );
}
